import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { MessageDraftDialog } from './MessageDraftDialog';
import { ReasonDialog } from './ReasonDialog';
import {
  MIN_PRICE_CHANGE_REASON,
  approveQuote,
  fetchOfferDraft,
  fetchQuoteBasis,
  moneyText,
  offerSent,
  proposeQuote,
  toErrorMessage,
  type InquiryDetail,
  type QuoteBasis,
} from './inquiriesApi';

const CURRENCIES = ['EUR', 'PLN'];
const zl = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('pl-PL')} zł`);

/** Zdanie o trasie: skąd liczba, km, czas jazdy, opłaty. */
export function routeText(route: QuoteBasis['route']): string {
  if (!route || route.km === null) return 'trasa nieznana';
  const source = route.source === 'here' ? 'wg map' : route.source === 'tms' ? 'wg TMS' : 'szacunek z punktów';
  const parts = [`${Math.round(route.km).toLocaleString('pl-PL')} km (${source})`];
  if (route.driveHours !== null) parts.push(`ok. ${route.driveHours.toFixed(1)} h jazdy`);
  if (route.tolls) {
    parts.push(route.tolls.pln !== null ? `opłaty drogowe ok. ${zl(route.tolls.pln)}` : `opłaty drogowe: ${route.tolls.note ?? 'nieznane'}`);
  } else {
    parts.push('bez opłat drogowych (trasy z map wyłączone)');
  }
  return parts.join(' · ');
}

/**
 * Wycena zapytania (makieta CS-07; 26.10.2026, P3, `inquiryQuotes`,
 * S-26.10-09). Podstawa z istniejących reguł — trasa, statystyka cen z TMS,
 * koszt własny najbliższego auta, korekta paliwowa, marża z pasmem. Cenników
 * nie mamy: wycena SPOT. CS proponuje cenę, zatwierdza dział Trade; oferta
 * dopiero po zatwierdzeniu, wysyła człowiek.
 */
export const InquiryQuotePanel = ({
  detail,
  onChange,
}: {
  detail: InquiryDetail;
  onChange: (d: InquiryDetail) => void;
}) => {
  const active = detail.quotes.find((q) => !q.superseded) ?? null;
  const older = detail.quotes.filter((q) => q.superseded);
  const [price, setPrice] = useState<string>(active ? String(active.price ?? '') : '');
  const [currency, setCurrency] = useState<string>(active?.currency ?? 'EUR');
  const [note, setNote] = useState('');
  const [basis, setBasis] = useState<QuoteBasis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approvePrice, setApprovePrice] = useState('');
  const [offerOpen, setOfferOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const n = Number(price.replace(',', '.'));
    const t = setTimeout(() => {
      fetchQuoteBasis(detail.id, Number.isFinite(n) && n > 0 ? n : null, currency)
        .then((b) => alive && setBasis(b))
        .catch((e) => alive && setError(toErrorMessage(e)));
    }, 500);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [detail.id, price, currency]);

  const propose = () =>
    void (async () => {
      setBusy(true);
      try {
        onChange(await proposeQuote(detail.id, Number(price.replace(',', '.')), currency, note || undefined));
        setNote('');
        setError(null);
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();

  const approvedChanged =
    active !== null && approvePrice.trim() !== '' && Number(approvePrice.replace(',', '.')) !== active.price;

  return (
    <SectionCard title="Wycena">
      <Box data-testid="inquiry-quote">
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
          <Box>
            <Typography variant="subtitle2">Trasa</Typography>
            <Typography variant="body2" data-testid="inquiry-quote-route">
              {basis ? (basis.route ? routeText(basis.route) : (basis.routeNote ?? 'trasa nieznana')) : '…'}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Reguły cenowe
            </Typography>
            <Typography variant="body2">{basis?.pricingRule ?? '…'}</Typography>
            {basis && basis.fuelSurcharges.length > 0 && (
              <Typography variant="body2">Korekta paliwowa: {basis.fuelSurcharges.join('; ')}</Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Referencje
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Historia cen z TMS</TableCell>
                  <TableCell data-testid="inquiry-quote-range">
                    {basis?.range
                      ? `${moneyText(basis.range.fromEur, 'EUR')} – ${moneyText(basis.range.toEur, 'EUR')} (mediana ${moneyText(basis.range.medianEur, 'EUR')})`
                      : (basis?.priceStatistics?.text ?? 'brak')}
                    {basis?.range && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {basis.range.basis}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Koszt własny floty</TableCell>
                  <TableCell data-testid="inquiry-quote-own-cost">
                    {basis?.ownCost ? (
                      <>
                        {zl(basis.ownCost.pln)}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {basis.ownCost.carName ?? `auto #${basis.ownCost.carId}`}, dolot {basis.ownCost.accessKm} km
                        </Typography>
                      </>
                    ) : (
                      'brak'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Historia klienta</TableCell>
                  <TableCell>
                    {basis
                      ? basis.clientHistory.total === 0
                        ? 'brak wcześniejszych zapytań'
                        : `przyjęte ${basis.clientHistory.accepted}, odrzucone ${basis.clientHistory.rejected}, bez odpowiedzi ${basis.clientHistory.noResponse}${basis.clientHistory.topRejectionReason ? ` (najczęściej: ${basis.clientHistory.topRejectionReason})` : ''}`
                      : '…'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {basis && basis.notes.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {basis.notes.join(' · ')}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2">Propozycja ceny</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <TextField
                size="small"
                label="Cena dla klienta (netto)"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={!detail.actions.propose}
                helperText={basis?.range ? `zakres z historii: ${basis.range.fromEur}–${basis.range.toEur} EUR` : 'zakresu nie ma — za mało danych'}
                slotProps={{ htmlInput: { 'data-testid': 'inquiry-quote-price', inputMode: 'decimal' } }}
              />
              <TextField
                select
                size="small"
                label="Waluta"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={!detail.actions.propose}
                sx={{ width: 100 }}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Table size="small" sx={{ mt: 1 }}>
              <TableBody>
                <TableRow>
                  <TableCell>Wartość w PLN</TableCell>
                  <TableCell>{zl(basis?.price?.valuePln)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Koszt własny</TableCell>
                  <TableCell>{zl(basis?.ownCost?.pln)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Marża</b>
                  </TableCell>
                  <TableCell data-testid="inquiry-quote-margin">
                    {basis?.margin?.pln !== null && basis?.margin?.pln !== undefined ? (
                      <b>
                        {zl(basis.margin.pln)}
                        {basis.margin.pct !== null ? ` (${basis.margin.pct.toLocaleString('pl-PL')}%)` : ''}
                      </b>
                    ) : basis?.margin ? (
                      `nieznana — brak: ${basis.margin.missing.join(', ')}`
                    ) : (
                      '—'
                    )}
                    {basis?.margin?.bandRule && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {basis.margin.bandRule}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {detail.actions.propose && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  label="Uwaga do wyceny (dla Trade)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ mt: 1 }}
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="contained"
                  disabled={busy || !(Number(price.replace(',', '.')) > 0)}
                  onClick={propose}
                  data-testid="inquiry-quote-propose"
                >
                  {active ? 'Zaproponuj nową cenę' : 'Zaproponuj cenę'}
                </Button>
              </>
            )}

            {active && (
              <Box sx={{ mt: 2 }} data-testid="inquiry-quote-active">
                <Typography variant="subtitle2">
                  Wycena {active.version}: {moneyText(active.approvedPrice ?? active.price, active.currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  zaproponował {active.proposedBy ?? '—'}, {dateTimeNoSeconds(active.proposedAt)}
                  {active.proposalNote ? ` · ${active.proposalNote}` : ''}
                </Typography>
                {active.approvedAt ? (
                  <Chip
                    sx={{ mt: 0.5 }}
                    color="success"
                    size="small"
                    label={`cena zatwierdzona: ${active.approvedBy ?? '—'} (${active.approvedRoleName ?? 'Trade'})`}
                    data-testid="inquiry-quote-approved"
                  />
                ) : (
                  <Chip sx={{ mt: 0.5 }} color="warning" size="small" label="czeka na zatwierdzenie ceny przez dział Trade" />
                )}
                {active.approvalNote && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Uwaga Trade: {active.approvalNote}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {detail.actions.approve && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setApprovePrice(String(active.price ?? ''));
                        setApproveOpen(true);
                      }}
                      data-testid="cs-wycena-zatwierdz"
                    >
                      Zatwierdź cenę
                    </Button>
                  )}
                  {(detail.actions.offerSent || active.offerSentAt) && (
                    <Button
                      variant="outlined"
                      disabled={!detail.actions.offerSent}
                      onClick={() => setOfferOpen(true)}
                      data-testid="cs-wycena-oferta"
                    >
                      {active.offerSentAt ? `Oferta wysłana ręcznie ✓ (${dateTimeNoSeconds(active.offerSentAt)})` : 'Przygotuj ofertę'}
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Cenę zatwierdza dział Trade. Oferty bez zatwierdzenia nie ma; wysyła ją człowiek ze swojej poczty.
                </Typography>
              </Box>
            )}
            {older.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Wcześniejsze wyceny:{' '}
                {older.map((q) => `${q.version}: ${moneyText(q.approvedPrice ?? q.price, q.currency)}`).join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {active && (
        <ReasonDialog
          open={approveOpen}
          title={`Zatwierdź cenę — wycena ${active.version}`}
          label={approvedChanged ? 'Powód zmiany ceny' : 'Uwaga (opcjonalnie)'}
          minLength={approvedChanged ? MIN_PRICE_CHANGE_REASON : 0}
          confirmLabel="Zatwierdź"
          confirmTestId="inquiry-quote-approve-confirm"
          description="Zatwierdzasz cenę dla klienta. Inna cena niż zaproponowana — tylko z powodem."
          extra={
            <TextField
              size="small"
              label="Cena zatwierdzona"
              value={approvePrice}
              onChange={(e) => setApprovePrice(e.target.value)}
              sx={{ mb: 1 }}
              helperText={`zaproponowana: ${moneyText(active.price, active.currency)}`}
            />
          }
          onClose={() => setApproveOpen(false)}
          onConfirm={async (text) => {
            const n = Number(approvePrice.replace(',', '.'));
            onChange(
              await approveQuote(detail.id, active.id, {
                ...(approvedChanged ? { price: n } : {}),
                ...(text ? { note: text } : {}),
              }),
            );
          }}
        />
      )}
      {active && (
        <MessageDraftDialog
          open={offerOpen}
          title={`Oferta dla klienta — ${detail.ref}`}
          load={(language) => fetchOfferDraft(detail.id, active.id, language)}
          onClose={() => setOfferOpen(false)}
          onSent={async (subject, body, language) => onChange(await offerSent(detail.id, active.id, subject, body, language))}
          sentTestId="inquiry-offer-sent"
        />
      )}
    </SectionCard>
  );
};
